import React, { useEffect, useState } from "react";
import axios from "axios";
import L from "leaflet";
import {
	MapContainer,
	Marker,
	Popup,
	Polyline,
	TileLayer,
	ZoomControl,
	useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { baseurladdress } from "../baseurl";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";

const statusPillStyle = (isComplete) => ({
	display: "inline-flex",
	alignItems: "center",
	gap: 8,
	padding: "6px 10px",
	borderRadius: 999,
	fontSize: 12,
	fontWeight: 700,
	color: isComplete ? "#166534" : "#991b1b",
	background: isComplete ? "#dcfce7" : "#fee2e2",
	border: `1px solid ${isComplete ? "#86efac" : "#fecaca"}`,
});

const pathDotStyle = (isComplete) => ({
	width: 10,
	height: 10,
	borderRadius: "50%",
	background: isComplete ? "#16a34a" : "#ef4444",
	boxShadow: `0 0 0 4px ${isComplete ? "rgba(22, 163, 74, 0.12)" : "rgba(239, 68, 68, 0.12)"}`,
});

const startIcon = new L.Icon({
	iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
	shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
	iconSize: [25, 41],
	iconAnchor: [12, 41],
});

const endIcon = new L.Icon({
	iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
	shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
	iconSize: [25, 41],
	iconAnchor: [12, 41],
});

const FitPath = ({ coords }) => {
	const map = useMap();

	useEffect(() => {
		if (coords.length > 0) {
			if (coords.length === 1) {
				map.setView(coords[0], 16);
			} else {
				map.fitBounds(coords, { padding: [40, 40], maxZoom: 17 });
			}
		}
	}, [coords, map]);

	return null;
};

const waypointIcon = (color, label) =>
	L.divIcon({
		className: "sessionreport-waypoint-icon",
		html: `
			<div style="
				width: 28px;
				height: 28px;
				border-radius: 50%;
				background: ${color};
				border: 3px solid white;
				box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
				display: flex;
				align-items: center;
				justify-content: center;
				color: white;
				font-weight: 800;
				font-size: 12px;
			">${label}</div>
		`,
		iconSize: [28, 28],
		iconAnchor: [14, 14],
		popupAnchor: [0, -14],
	});

const SAMPLE_SESSION_ROWS = [
	{
		staff_id: 101,
		staff_name: "Akhil Mathew",
		sessions: [
			{ sessionId: 90011, isComplete: true },
			{ sessionId: 90012, isComplete: false },
		],
	},
	{
		staff_id: 102,
		staff_name: "Nandana S",
		sessions: [
			{ sessionId: 90021, isComplete: true },
		],
	},
	{
		staff_id: 103,
		staff_name: "Midhun Raj",
		sessions: [
			{ sessionId: 90031, isComplete: false },
			{ sessionId: 90032, isComplete: false },
			{ sessionId: 90033, isComplete: true },
		],
	},
];

const SAMPLE_PATHS = {
	90011: {
		locations: [
			{ lat: 10.015, lng: 76.315 },
			{ lat: 10.019, lng: 76.318 },
			{ lat: 10.024, lng: 76.323 },
		],
	},
	90012: {
		locations: [
			{ lat: 10.028, lng: 76.327 },
			{ lat: 10.031, lng: 76.332 },
		],
	},
	90021: {
		locations: [
			{ lat: 10.04, lng: 76.29 },
			{ lat: 10.043, lng: 76.295 },
			{ lat: 10.048, lng: 76.302 },
		],
	},
	90031: {
		locations: [
			{ lat: 10.055, lng: 76.28 },
			{ lat: 10.058, lng: 76.286 },
		],
	},
	90032: {
		locations: [
			{ lat: 10.065, lng: 76.272 },
			{ lat: 10.071, lng: 76.278 },
			{ lat: 10.074, lng: 76.284 },
		],
	},
	90033: {
		locations: [
			{ lat: 10.08, lng: 76.29 },
			{ lat: 10.084, lng: 76.294 },
		],
	},
};

const normalizeWaypoint = (waypoint, index) => {
	const latitude = Number(
		waypoint.lat ?? waypoint.latitude ?? waypoint.waypoint_lat ?? waypoint.waypointLatitude ?? waypoint.y
	);
	const longitude = Number(
		waypoint.lng ?? waypoint.longitude ?? waypoint.waypoint_lng ?? waypoint.waypointLongitude ?? waypoint.x
	);

	return {
		id: waypoint.id ?? waypoint.waypoint_id ?? `${index}`,
		name: waypoint.name ?? waypoint.waypoint_name ?? waypoint.title ?? `Stop ${index + 1}`,
		lat: latitude,
		lng: longitude,
		order: Number(
			waypoint.waypoint_order ?? waypoint.order ?? waypoint.seq ?? waypoint.sequence ?? index + 1
		),
	};
};

const normalizeRoutePoint = (pair) => {
	if (!pair) return null;

	if (Array.isArray(pair)) {
		const lng = Number(pair[0]);
		const lat = Number(pair[1]);
		if (Number.isFinite(lat) && Number.isFinite(lng)) {
			return [lat, lng];
		}
		return null;
	}

	const lat = Number(pair.lat ?? pair.latitude ?? pair.y);
	const lng = Number(pair.lng ?? pair.longitude ?? pair.x);
	if (Number.isFinite(lat) && Number.isFinite(lng)) {
		return [lat, lng];
	}

	return null;
};

const extractRouteCoordsFromValue = (value) => {
	if (!value) return null;

	let geometry = value;
	if (typeof geometry === "string") {
		try {
			geometry = JSON.parse(geometry);
		} catch {
			return null;
		}
	}

	if (Array.isArray(geometry)) {
		const coords = geometry.map(normalizeRoutePoint).filter(Boolean);
		return coords.length > 1 ? coords : null;
	}

	if (Array.isArray(geometry?.coordinates)) {
		const coords = geometry.coordinates.map(normalizeRoutePoint).filter(Boolean);
		return coords.length > 1 ? coords : null;
	}

	if (Array.isArray(geometry?.routes) && geometry.routes.length > 0) {
		return extractRouteCoordsFromValue(geometry.routes[0]?.geometry);
	}

	if (geometry?.type === "LineString" && Array.isArray(geometry.coordinates)) {
		const coords = geometry.coordinates.map(normalizeRoutePoint).filter(Boolean);
		return coords.length > 1 ? coords : null;
	}

	return null;
};

const extractRouteGeometry = (trip) => {
	const candidates = [
		trip?.routeCoords,
		trip?.route_coords,
		trip?.route_geometry,
		trip?.geometry,
		trip?.route,
		trip?.routes?.[0]?.geometry,
	];

	for (const candidate of candidates) {
		const coords = extractRouteCoordsFromValue(candidate);
		if (coords) return coords;
	}

	return null;
};

const extractPathData = (payload) => {
	const trip = payload?.trip || payload?.data?.trip || payload || {};
	const waypoints = Array.isArray(payload?.waypoints) ? payload.waypoints : [];
	const normalizedWaypoints = waypoints
		.map(normalizeWaypoint)
		.filter((waypoint) => Number.isFinite(waypoint.lat) && Number.isFinite(waypoint.lng))
		.sort((a, b) => (a.order || 0) - (b.order || 0));
	const routeCoords =
		extractRouteGeometry(payload) ||
		extractRouteGeometry(trip) ||
		extractRouteCoordsFromValue(payload?.routeCoords) ||
		extractRouteCoordsFromValue(payload?.route_geometry) ||
		extractRouteCoordsFromValue(payload?.geometry) ||
		null;

	return {
		trip,
		waypoints: normalizedWaypoints,
		routeCoords,
		distance: trip?.distance != null ? Number(trip.distance) : null,
		duration: trip?.duration != null ? Number(trip.duration) : null,
	};
};

const inferPathFromWaypoints = (waypoints) => {
	const coords = waypoints.map((waypoint) => [Number(waypoint.lat), Number(waypoint.lng)]);
	return {
		coords,
		start: coords[0] || null,
		end: coords.length > 1 ? coords[coords.length - 1] : null,
	};
};

const toDateValue = (date) => {
	const value = new Date(date);
	if (Number.isNaN(value.getTime())) return "";
	const year = value.getFullYear();
	const month = String(value.getMonth() + 1).padStart(2, "0");
	const day = String(value.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const formatDuration = (minutes) => {
	if (!Number.isFinite(minutes)) return null;
	if (minutes < 60) return `${Math.round(minutes)} min`;
	const hours = Math.floor(minutes / 60);
	const mins = Math.round(minutes % 60);
	return `${hours} hr ${mins} min`;
};

const getSessionLookupId = (session) =>
	session?.id ?? session?.sessionId ?? session?.session_id ?? session?.sessionID ?? null;

const getSessionStatusLabel = (session) => (session?.isComplete ? "Completed" : "Open");

const generateSessionPDF = async (staff, session, sessionData) => {
	const doc = new jsPDF();
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	
	// Colors
	const primaryColor = [29, 78, 216]; // #1d4ed8
	const secondaryColor = [15, 23, 42]; // #0f172a
	const successColor = [22, 163, 74]; // #16a34a
	const warningColor = [239, 68, 68]; // #ef4444
	
	// Header section
	doc.setFillColor(...primaryColor);
	doc.rect(0, 0, pageWidth, 40, 'F');
	
	doc.setTextColor(255, 255, 255);
	doc.setFontSize(20);
	doc.setFont('helvetica', 'bold');
	doc.text('Session Report', 14, 20);
	
	doc.setFontSize(10);
	doc.setFont('helvetica', 'normal');
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
	
	// Session ID in header
	doc.setFontSize(12);
	doc.setFont('helvetica', 'bold');
	doc.text(`Session ID: ${session.sessionId}`, pageWidth - 14, 20, { align: 'right' });
	
	// Staff Information Section
	let yPos = 50;
	doc.setFillColor(248, 250, 252);
	doc.rect(14, yPos - 5, pageWidth - 28, 25, 'F');
	
	doc.setTextColor(...secondaryColor);
	doc.setFontSize(14);
	doc.setFont('helvetica', 'bold');
	doc.text('Staff Information', 14, yPos + 5);
	
	doc.setFontSize(11);
	doc.setFont('helvetica', 'normal');
	doc.text(`Name: ${staff.staff_name}`, 14, yPos + 15);
	doc.text(`Staff ID: ${staff.staff_id}`, 80, yPos + 15);
	
	yPos += 30;
	
	// Session Details Section
	doc.setFillColor(248, 250, 252);
	doc.rect(14, yPos - 5, pageWidth - 28, 35, 'F');
	
	doc.setTextColor(...secondaryColor);
	doc.setFontSize(14);
	doc.setFont('helvetica', 'bold');
	doc.text('Session Details', 14, yPos + 5);
	
	doc.setFontSize(11);
	doc.setFont('helvetica', 'normal');
	const statusColor = session.isComplete ? successColor : warningColor;
	doc.setTextColor(...statusColor);
	doc.text(`Status: ${session.isComplete ? 'Completed' : 'Open'}`, 14, yPos + 15);
	
	doc.setTextColor(...secondaryColor);
	doc.text(`Session ID: ${session.sessionId}`, 70, yPos + 15);
	
	// Trip details if available
	if (sessionData) {
		const distance = sessionData.distance;
		const duration = sessionData.duration;
		const waypoints = sessionData.waypoints || [];
		
		if (Number.isFinite(distance)) {
			doc.text(`Distance: ${distance.toFixed(2)} km`, 14, yPos + 25);
		}
		if (Number.isFinite(duration)) {
			doc.text(`Duration: ${formatDuration(duration)}`, 70, yPos + 25);
		}
		doc.text(`Waypoints: ${waypoints.length}`, 130, yPos + 25);
	}
	
	yPos += 40;
	
	// Waypoints Table
	if (sessionData && sessionData.waypoints && sessionData.waypoints.length > 0) {
		doc.setTextColor(...secondaryColor);
		doc.setFontSize(14);
		doc.setFont('helvetica', 'bold');
		doc.text('Waypoints', 14, yPos);
		
		yPos += 10;
		
		const tableData = sessionData.waypoints.map((waypoint, index) => [
			index + 1,
			waypoint.name || `Stop ${index + 1}`,
			waypoint.lat?.toFixed(6) || 'N/A',
			waypoint.lng?.toFixed(6) || 'N/A',
			waypoint.order || index + 1
		]);

		autoTable(doc, {
			startY: yPos,
			head: [['#', 'Name', 'Latitude', 'Longitude', 'Order']],
			body: tableData,
			theme: 'striped',
			headStyles: {
				fillColor: primaryColor,
				textColor: 255,
				fontStyle: 'bold',
				fontSize: 10
			},
			bodyStyles: {
				fontSize: 9,
				textColor: secondaryColor
			},
			alternateRowStyles: {
				fillColor: [248, 250, 252]
			},
			margin: { top: 10, left: 14, right: 14 },
			tableWidth: pageWidth - 28
		});

		yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 50;
	} else {
		doc.setTextColor(100);
		doc.setFontSize(10);
		doc.setFont('helvetica', 'italic');
		doc.text('No waypoint data available for this session.', 14, yPos);
		yPos += 15;
	}
	
	// Summary Statistics
	if (sessionData) {
		doc.setFillColor(248, 250, 252);
		doc.rect(14, yPos - 5, pageWidth - 28, 25, 'F');
		
		doc.setTextColor(...secondaryColor);
		doc.setFontSize(14);
		doc.setFont('helvetica', 'bold');
		doc.text('Summary', 14, yPos + 5);
		
		doc.setFontSize(11);
		doc.setFont('helvetica', 'normal');
		
		const stats = [];
		if (sessionData.waypoints) {
			stats.push(`Total Waypoints: ${sessionData.waypoints.length}`);
		}
		if (Number.isFinite(sessionData.distance)) {
			stats.push(`Total Distance: ${sessionData.distance.toFixed(2)} km`);
		}
		if (Number.isFinite(sessionData.duration)) {
			stats.push(`Total Duration: ${formatDuration(sessionData.duration)}`);
		}
		
		stats.forEach((stat, index) => {
			doc.text(stat, 14 + (index * 70), yPos + 15);
		});
		
		yPos += 30;
	}
	
	// Footer
	doc.setFontSize(8);
	doc.setTextColor(150);
	doc.setFont('helvetica', 'normal');
	doc.text('This report was automatically generated by the Session Tracking System', pageWidth / 2, pageHeight - 10, { align: 'center' });
	
	// Save the PDF
	const fileName = `session_report_${staff.staff_name.replace(/\s+/g, '_')}_${session.sessionId}.pdf`;
	doc.save(fileName);
};

const generateDailyStaffPDF = async (staff, sessionsWithData, selectedDate) => {
	const doc = new jsPDF();
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	const primaryColor = [29, 78, 216];
	const secondaryColor = [15, 23, 42];
	const successColor = [22, 163, 74];
	const warningColor = [239, 68, 68];

	const safeName = String(staff?.staff_name || "staff").replace(/\s+/g, "_");
	let yPos = 18;

	const addHeader = (title) => {
		doc.setFillColor(...primaryColor);
		doc.rect(0, 0, pageWidth, 22, "F");
		doc.setTextColor(255, 255, 255);
		doc.setFontSize(16);
		doc.setFont("helvetica", "bold");
		doc.text(title, 14, 14);
	};

	const ensureSpace = (neededHeight) => {
		if (yPos + neededHeight > pageHeight - 16) {
			doc.addPage();
			addHeader("Daily Session Report");
			yPos = 30;
		}
	};

	addHeader("Daily Session Report");
	doc.setTextColor(...secondaryColor);
	doc.setFontSize(11);
	doc.setFont("helvetica", "normal");
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
	doc.text(`Date: ${selectedDate}`, 14, 38);
	doc.text(`Staff: ${staff?.staff_name || "-"}`, 14, 46);
	doc.text(`Staff ID: ${staff?.staff_id ?? "-"}`, 95, 46);

	yPos = 58;

	const totalSessions = sessionsWithData.length;
	const completedSessions = sessionsWithData.filter(({ session }) => session?.isComplete).length;
	const openSessions = totalSessions - completedSessions;

	doc.setFillColor(248, 250, 252);
	doc.rect(14, yPos - 5, pageWidth - 28, 22, "F");
	doc.setTextColor(...secondaryColor);
	doc.setFontSize(13);
	doc.setFont("helvetica", "bold");
	doc.text("Summary", 14, yPos + 4);

	doc.setFontSize(10.5);
	doc.setFont("helvetica", "normal");
	doc.text(`Total Sessions: ${totalSessions}`, 14, yPos + 12);
	doc.setTextColor(...successColor);
	doc.text(`Completed: ${completedSessions}`, 76, yPos + 12);
	doc.setTextColor(...warningColor);
	doc.text(`Open: ${openSessions}`, 120, yPos + 12);

	yPos += 28;

	if (sessionsWithData.length === 0) {
		doc.setTextColor(100);
		doc.setFontSize(10);
		doc.setFont("helvetica", "italic");
		doc.text("No sessions recorded for this staff member on the selected date.", 14, yPos);
		doc.save(`daily_report_${safeName}_${selectedDate}.pdf`);
		return;
	}

	for (let index = 0; index < sessionsWithData.length; index++) {
		const { session, sessionData } = sessionsWithData[index];
		const sessionId = session?.sessionId ?? session?.session_id ?? session?.id ?? index + 1;
		const statusColor = session?.isComplete ? successColor : warningColor;
		const waypoints = sessionData?.waypoints || [];
		const distance = sessionData?.distance;
		const duration = sessionData?.duration;

		ensureSpace(58 + Math.max(waypoints.length * 8, 18));

		doc.setFillColor(248, 250, 252);
		doc.rect(14, yPos - 5, pageWidth - 28, 20, "F");
		doc.setTextColor(...secondaryColor);
		doc.setFontSize(12.5);
		doc.setFont("helvetica", "bold");
		doc.text(`Session ${index + 1}`, 14, yPos + 3);
		doc.setTextColor(...statusColor);
		doc.text(getSessionStatusLabel(session), pageWidth - 14, yPos + 3, { align: "right" });

		doc.setTextColor(...secondaryColor);
		doc.setFontSize(10.5);
		doc.setFont("helvetica", "normal");
		doc.text(`Session ID: ${sessionId}`, 14, yPos + 11);

		const meta = [];
		if (Number.isFinite(distance)) meta.push(`Distance: ${distance.toFixed(2)} km`);
		if (Number.isFinite(duration)) meta.push(`Duration: ${formatDuration(duration)}`);
		meta.push(`Waypoints: ${waypoints.length}`);
		doc.text(meta.join("   •   "), 14, yPos + 18);

		yPos += 24;

		if (waypoints.length > 0) {
			const tableData = waypoints.map((waypoint, wpIndex) => [
				wpIndex + 1,
				waypoint.name || `Stop ${wpIndex + 1}`,
				waypoint.lat?.toFixed(6) || "N/A",
				waypoint.lng?.toFixed(6) || "N/A",
				waypoint.order || wpIndex + 1,
			]);

			autoTable(doc, {
				startY: yPos,
				head: [["#", "Name", "Latitude", "Longitude", "Order"]],
				body: tableData,
				theme: "striped",
				headStyles: {
					fillColor: primaryColor,
					textColor: 255,
					fontStyle: "bold",
					fontSize: 9,
				},
				bodyStyles: {
					fontSize: 8.5,
					textColor: secondaryColor,
				},
				alternateRowStyles: {
					fillColor: [248, 250, 252],
				},
				margin: { top: 8, left: 14, right: 14 },
				tableWidth: pageWidth - 28,
			});

			yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 40;
		} else {
			doc.setTextColor(100);
			doc.setFontSize(10);
			doc.setFont("helvetica", "italic");
			doc.text("No waypoint data available for this session.", 14, yPos);
			yPos += 16;
		}

		if (index < sessionsWithData.length - 1) {
			ensureSpace(12);
			doc.setDrawColor(226, 232, 240);
			doc.line(14, yPos - 2, pageWidth - 14, yPos - 2);
			yPos += 8;
		}
	}

	doc.setFontSize(8);
	doc.setTextColor(150);
	doc.setFont("helvetica", "normal");
	doc.text(
		"This report was automatically generated by the Session Tracking System",
		pageWidth / 2,
		pageHeight - 10,
		{ align: "center" }
	);

	doc.save(`daily_report_${safeName}_${selectedDate}.pdf`);
};

const PathModal = ({ open, onClose, sessionData, loading }) => {
	if (!open) return null;

	const waypoints = sessionData?.waypoints || sessionData?.locations || [];
	const inferred = inferPathFromWaypoints(waypoints);
	// Prefer the OSRM-calculated road route (real driving path) when available;
	// fall back to straight lines between the recorded waypoints otherwise.
	const routeCoords = sessionData?.routeCoords;
	const usingRoadRoute = Array.isArray(routeCoords) && routeCoords.length > 1;
	const coords = usingRoadRoute ? routeCoords : inferred.coords;
	const startPoint = inferred.start;
	const endPoint = inferred.end;
	const distance = sessionData?.distance;
	const duration = sessionData?.duration;

	return (
		<div
			style={{
				position: "fixed",
				inset: 0,
				background: "rgba(15, 23, 42, 0.55)",
				zIndex: 9999,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: 16,
			}}
			onClick={onClose}
		>
			<div
				style={{
					width: "min(980px, 100%)",
					background: "#fff",
					borderRadius: 18,
					overflow: "hidden",
					boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)",
				}}
				onClick={(event) => event.stopPropagation()}
			>
				<div style={{ padding: "16px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
					<div>
						<div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Path Preview</div>
						<div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{sessionData?.title || "Session"}</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						style={{
							border: "none",
							background: "#f1f5f9",
							color: "#0f172a",
							borderRadius: 999,
							width: 36,
							height: 36,
							cursor: "pointer",
							fontSize: 18,
							fontWeight: 700,
						}}
					>
						×
					</button>
				</div>

				<div style={{ padding: 18 }}>
					{sessionData?.trip && (
						<div style={{ marginBottom: 14, padding: 14, borderRadius: 14, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
							<div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Trip Details</div>
							<div style={{ marginTop: 4, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
								{sessionData.trip.trip_name || sessionData.title || "Session Trip"}
							</div>
							<div style={{ marginTop: 4, color: "#475569", fontSize: 13 }}>
								Waypoints: {waypoints.length}
							</div>
							{startPoint && endPoint && (
								<div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
									Start: {startPoint[0].toFixed(4)}, {startPoint[1].toFixed(4)} · End: {endPoint[0].toFixed(4)}, {endPoint[1].toFixed(4)}
								</div>
							)}
						</div>
					)}

					{(Number.isFinite(distance) || Number.isFinite(duration)) && (
						<div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
							<div style={{ flex: "1 1 120px", padding: 12, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
								<div style={{ fontSize: 11, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.6 }}>Stops</div>
								<div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{waypoints.length}</div>
							</div>
							{Number.isFinite(distance) && (
								<div style={{ flex: "1 1 120px", padding: 12, borderRadius: 12, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
									<div style={{ fontSize: 11, color: "#15803d", textTransform: "uppercase", letterSpacing: 0.6 }}>Distance</div>
									<div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{distance.toFixed(2)} km</div>
								</div>
							)}
							{Number.isFinite(duration) && (
								<div style={{ flex: "1 1 120px", padding: 12, borderRadius: 12, background: "#fefce8", border: "1px solid #fde68a" }}>
									<div style={{ fontSize: 11, color: "#a16207", textTransform: "uppercase", letterSpacing: 0.6 }}>Duration</div>
									<div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{formatDuration(duration)}</div>
								</div>
							)}
							<div style={{ flex: "1 1 120px", padding: 12, borderRadius: 12, background: usingRoadRoute ? "#f5f3ff" : "#fff7ed", border: `1px solid ${usingRoadRoute ? "#ddd6fe" : "#fed7aa"}` }}>
								<div style={{ fontSize: 11, color: usingRoadRoute ? "#6d28d9" : "#c2410c", textTransform: "uppercase", letterSpacing: 0.6 }}>Route Type</div>
								<div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{usingRoadRoute ? "Road route (OSRM)" : "Straight line"}</div>
							</div>
						</div>
					)}

					{loading ? (
						<div style={{ padding: 40, textAlign: "center", color: "#475569" }}>Loading path...</div>
					) : coords.length === 0 ? (
						<div style={{ padding: 40, textAlign: "center", color: "#b45309", background: "#fff7ed", borderRadius: 14 }}>
							No path data available for this session.
						</div>
					) : (
						<div style={{ height: 420, borderRadius: 14, overflow: "hidden", border: "1px solid #e5e7eb" }}>
							<MapContainer center={coords[0]} zoom={14} style={{ height: "100%", width: "100%" }} zoomControl={false}>
								<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
								<Polyline positions={coords} color="#2563eb" weight={4} opacity={0.9} />
								<Marker position={coords[0]} icon={startIcon}>
									<Popup>Session start</Popup>
								</Marker>
								{coords.length > 1 && (
									<Marker position={coords[coords.length - 1]} icon={endIcon}>
										<Popup>Session end</Popup>
									</Marker>
								)}
								{waypoints.map((waypoint, index) => (
									<Marker
										key={waypoint.id}
										position={[waypoint.lat, waypoint.lng]}
										icon={waypointIcon(index === 0 ? "#16a34a" : index === waypoints.length - 1 ? "#dc2626" : "#2563eb", String(index + 1))}
									>
										<Popup>
											<div style={{ fontSize: 13, lineHeight: 1.6 }}>
												<div style={{ fontWeight: 800, color: "#0f172a" }}>{waypoint.name}</div>
												<div style={{ color: "#64748b" }}>Order: {waypoint.order || index + 1}</div>
												<div>Lat: {waypoint.lat.toFixed(6)}</div>
												<div>Lng: {waypoint.lng.toFixed(6)}</div>
											</div>
										</Popup>
									</Marker>
								))}
								<FitPath coords={coords} />
								<ZoomControl position="topright" />
							</MapContainer>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const SessionReport = () => {
	const token = localStorage.getItem("token");
	const [selectedDate, setSelectedDate] = useState(toDateValue(new Date()));
	const [staffRows, setStaffRows] = useState([]);
	const [expandedStaffId, setExpandedStaffId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [pathModalOpen, setPathModalOpen] = useState(false);
	const [pathLoading, setPathLoading] = useState(false);
	const [selectedPath, setSelectedPath] = useState(null);
	const [selectedPathTitle, setSelectedPathTitle] = useState("");

	const fetchDailySessions = async (date) => {
		setLoading(true);
		setError("");

		try {
			const response = await axios.get(`${baseurladdress}sessions/getsessionsbydate`, {
				params: { date },
				headers: { Authorization: `Bearer ${token}` },
			});
			const rawRows = Array.isArray(response.data?.data)
				? response.data.data
				: Array.isArray(response.data)
					? response.data
					: [];

			setStaffRows(rawRows);
			setExpandedStaffId((current) => current || rawRows[0]?.staff_id || null);
		} catch (fetchError) {
			console.error(fetchError);
			// Fall back to sample data so the page still renders something
			// useful (e.g. local development without a reachable backend).
			const rawRows = SAMPLE_SESSION_ROWS;
			setStaffRows(rawRows);
			setExpandedStaffId((current) => current || rawRows[0]?.staff_id || null);
			setError(fetchError?.response?.data?.message || "Failed to load session report for the selected date.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchDailySessions(selectedDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDate]);

	const handleDateChange = (event) => {
		const nextDate = event.target.value;
		setSelectedDate(nextDate);
		setExpandedStaffId(null);
	};

	const handleDailyPdf = async () => {
		try {
			if (!staffRows.length) {
				window.alert("No staff sessions available for the selected date.");
				return;
			}

			const sessionsWithData = await Promise.all(
				staffRows.flatMap((staff) =>
					(staff.sessions || []).map(async (session) => {
						const sessionLookupId = getSessionLookupId(session);
						let sessionData = null;

						if (sessionLookupId) {
							try {
								const response = await axios.get(
									`${baseurladdress}sessions/gettripdetails/${sessionLookupId}`,
									{
										headers: { Authorization: `Bearer ${token}` },
									}
								);
								sessionData = extractPathData(response.data?.data || response.data || {});
							} catch (fetchError) {
								console.error("Failed to fetch session data for daily PDF:", fetchError);
							}
						}

						return { staff, session, sessionData };
					})
				)
			);

			await generateDailyStaffPDF(
				{ staff_name: "All Staff", staff_id: "-" },
				sessionsWithData,
				selectedDate
			);
		} catch (error) {
			console.error("Error generating daily PDF:", error);
			window.alert("Failed to generate daily PDF report. Please try again.");
		}
	};

	const handleStaffPdf = async (staff) => {
		try {
			const sessions = Array.isArray(staff?.sessions) ? staff.sessions : [];
			const sessionsWithData = await Promise.all(
				sessions.map(async (session) => {
					const sessionLookupId = getSessionLookupId(session);
					let sessionData = null;

					if (sessionLookupId) {
						try {
							const response = await axios.get(
								`${baseurladdress}sessions/gettripdetails/${sessionLookupId}`,
								{
									headers: { Authorization: `Bearer ${token}` },
								}
							);
							sessionData = extractPathData(response.data?.data || response.data || {});
						} catch (fetchError) {
							console.error("Failed to fetch session data for daily PDF:", fetchError);
						}
					}

					return { staff, session, sessionData };
				})
			);

			await generateDailyStaffPDF(staff, sessionsWithData, selectedDate);
		} catch (error) {
			console.error("Error generating daily PDF:", error);
			window.alert("Failed to generate daily PDF report. Please try again.");
		}
	};

	const handleSessionPdf = async (staff, session) => {
		try {
			const sessionLookupId = getSessionLookupId(session);
			
			// Fetch session data
			let sessionData = null;
			if (sessionLookupId) {
				try {
					const response = await axios.get(
						`${baseurladdress}sessions/gettripdetails/${sessionLookupId}`,
						{
							headers: { Authorization: `Bearer ${token}` },
						}
					);
					sessionData = extractPathData(response.data?.data || response.data || {});
				} catch (fetchError) {
					console.error('Failed to fetch session data for PDF:', fetchError);
					// Continue with basic session data even if detailed data fails
				}
			}
			
			// Generate PDF with available data
			await generateSessionPDF(staff, session, sessionData);
		} catch (error) {
			console.error('Error generating PDF:', error);
			window.alert('Failed to generate PDF report. Please try again.');
		}
	};

	const handleViewPath = async (staff, session) => {
		const sessionLookupId = getSessionLookupId(session);
		const sessionDisplayId = sessionLookupId ?? session.sessionId;
		setSelectedPathTitle(`${staff.staff_name} - Session ${sessionDisplayId}`);
		setPathModalOpen(true);
		setPathLoading(true);
		setSelectedPath(null);

		try {
			if (!sessionLookupId) {
				throw new Error("Missing session identifier for path lookup");
			}

			const response = await axios.get(
				`${baseurladdress}sessions/gettripdetails/${sessionLookupId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			const pathData = extractPathData(response.data?.data || response.data || {});
			setSelectedPath(pathData);
		} catch (viewError) {
			console.error(viewError);
			const fallback = SAMPLE_PATHS[sessionLookupId ?? session.sessionId] || { locations: [] };
			setSelectedPath({
				trip: {},
				locations: fallback.locations || [],
				waypoints: fallback.locations || [],
			});
		} finally {
			setPathLoading(false);
		}
	};

	const containerStyle = {
		maxWidth: 1400,
		margin: "0 auto",
		padding: 20,
	};

	const headerStyle = {
		background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
		color: "white",
		borderRadius: 20,
		padding: 24,
		boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
	};

	const staffCount = staffRows.length;
	const sessionCount = staffRows.reduce((sum, staff) => sum + staff.sessions.length, 0);

	return (
		<div style={containerStyle}>
			<PathModal
				open={pathModalOpen}
				onClose={() => setPathModalOpen(false)}
				sessionData={selectedPath ? { ...selectedPath, title: selectedPathTitle } : null}
				loading={pathLoading}
			/>

			<div style={headerStyle}>
				<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
					<div>
						<div style={{ fontSize: 12, letterSpacing: 1.1, textTransform: "uppercase", opacity: 0.8 }}>Tracking</div>
						<h3 style={{ margin: "6px 0 0", fontSize: 28, fontWeight: 800 }}>Session Report</h3>
						<div style={{ marginTop: 8, color: "rgba(255,255,255,0.82)", fontSize: 14 }}>
							Daily staff session summary for the selected date.
						</div>
					</div>

					<div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
						<div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.1)", padding: "10px 12px", borderRadius: 14 }}>
							<label htmlFor="session-report-date" style={{ fontSize: 13, fontWeight: 700 }}>Date</label>
							<input
								id="session-report-date"
								type="date"
								value={selectedDate}
								onChange={handleDateChange}
								style={{
									border: "1px solid rgba(255,255,255,0.28)",
									background: "white",
									color: "#0f172a",
									padding: "8px 10px",
									borderRadius: 10,
									fontWeight: 600,
								}}
							/>
						</div>

						<button
							type="button"
							onClick={handleDailyPdf}
							style={{
								border: "none",
								background: "#ffffff",
								color: "#1d4ed8",
								padding: "11px 16px",
								borderRadius: 14,
								fontWeight: 800,
								cursor: "pointer",
								boxShadow: "0 8px 20px rgba(255,255,255,0.12)",
							}}
						>
							Download Daily Report PDF
						</button>
					</div>
				</div>

				<div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 20 }}>
					<div style={{ minWidth: 160, flex: "1 1 160px", background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 14 }}>
						<div style={{ fontSize: 12, opacity: 0.78 }}>Staff</div>
						<div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{staffCount}</div>
					</div>
					<div style={{ minWidth: 160, flex: "1 1 160px", background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 14 }}>
						<div style={{ fontSize: 12, opacity: 0.78 }}>Sessions</div>
						<div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{sessionCount}</div>
					</div>
					<div style={{ minWidth: 160, flex: "1 1 160px", background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 14 }}>
						<div style={{ fontSize: 12, opacity: 0.78 }}>Selected Date</div>
						<div style={{ fontSize: 18, fontWeight: 800, marginTop: 8 }}>{selectedDate || "-"}</div>
					</div>
				</div>
			</div>

			<div style={{ marginTop: 20 }}>
				{error && (
					<div style={{ padding: 14, borderRadius: 14, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", marginBottom: 16 }}>
						{error}
					</div>
				)}

				{loading ? (
					<div style={{ padding: 60, textAlign: "center", background: "white", borderRadius: 20, color: "#475569", boxShadow: "0 8px 30px rgba(15, 23, 42, 0.08)" }}>
						Loading sessions...
					</div>
				) : staffRows.length === 0 ? (
					<div style={{ padding: 60, textAlign: "center", background: "white", borderRadius: 20, color: "#64748b", boxShadow: "0 8px 30px rgba(15, 23, 42, 0.08)" }}>
						No sessions found for {selectedDate}.
					</div>
				) : (
					<div style={{ display: "grid", gap: 14 }}>
						{staffRows.map((staff) => {
							const isExpanded = expandedStaffId === staff.staff_id;

							return (
								<div key={staff.staff_id} style={{ background: "white", borderRadius: 18, overflow: "hidden", boxShadow: "0 8px 30px rgba(15, 23, 42, 0.08)", border: "1px solid #e5e7eb" }}>
									<div
										role="button"
										tabIndex={0}
										onClick={() => setExpandedStaffId(isExpanded ? null : staff.staff_id)}
										onKeyDown={(event) => {
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												setExpandedStaffId(isExpanded ? null : staff.staff_id);
											}
										}}
										style={{
											width: "100%",
											border: "none",
											background: isExpanded ? "#eff6ff" : "#f8fafc",
											padding: "16px 18px",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 12,
											cursor: "pointer",
											textAlign: "left",
										}}
									>
										<div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
											<div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg, #1d4ed8, #2563eb)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
												{staff.staff_name?.charAt(0)?.toUpperCase() || "S"}
											</div>
											<div style={{ minWidth: 0 }}>
												<div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
													{staff.staff_name}
												</div>
												<div style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
													{staff.sessions.length} session(s)
												</div>
											</div>
										</div>

										<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
											<button
												type="button"
												onClick={(event) => {
													event.stopPropagation();
													handleStaffPdf(staff);
												}}
												style={{
													border: "1px solid #cbd5e1",
													background: "white",
													color: "#0f172a",
													padding: "9px 12px",
													borderRadius: 12,
													cursor: "pointer",
													fontWeight: 700,
												}}
											>
												Download Daily PDF
											</button>
											<span style={{ color: "#1d4ed8", fontSize: 18, fontWeight: 800 }}>{isExpanded ? "−" : "+"}</span>
										</div>
									</div>

									{isExpanded && (
										<div style={{ padding: 18, borderTop: "1px solid #e5e7eb", background: "#ffffff" }}>
											{staff.sessions.length === 0 ? (
												<div style={{ padding: 16, background: "#f8fafc", borderRadius: 14, color: "#64748b" }}>
													No sessions recorded for this staff member on the selected date.
												</div>
											) : (
												<div style={{ display: "grid", gap: 12 }}>
													{staff.sessions.map((session, index) => (
														<div key={`${staff.staff_id}-${session.sessionId}`} style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 14, background: "#fdfdfd" }}>
															<div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
																<div style={{ minWidth: 220 }}>
																	<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
																		<div style={pathDotStyle(session.isComplete)} />
																		<div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
																			Session {index + 1}
																		</div>
																		<div style={statusPillStyle(session.isComplete)}>
																			{session.isComplete ? "Completed" : "Open"}
																		</div>
																	</div>
																	<div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
																		Session ID: {session.sessionId}
																	</div>
																</div>

																<div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
																	<button
																		type="button"
																		onClick={() => handleViewPath(staff, session)}
																		style={{
																			border: "none",
																			background: "#2563eb",
																			color: "white",
																			padding: "10px 14px",
																			borderRadius: 12,
																			cursor: "pointer",
																			fontWeight: 700,
																		}}
																	>
																		View Path
																	</button>
																	<button
																		type="button"
																		onClick={() => handleSessionPdf(staff, session)}
																		style={{
																			border: "1px solid #cbd5e1",
																			background: "white",
																			color: "#0f172a",
																			padding: "10px 14px",
																			borderRadius: 12,
																			cursor: "pointer",
																			fontWeight: 700,
																		}}
																	>
																		Download Report
																	</button>
																</div>
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default SessionReport;
